// Πράξεις του editor κάτοψης πάνω στο FloorDoc. Καθαρές συναρτήσεις: η οθόνη κρατά μόνο
// το έγγραφο και το ιστορικό του (Αναίρεση/Επανάληψη).
//
// ΚΟΙΝΟ ΑΡΧΕΙΟ, πανομοιότυπο σε Sala (src/lib) και ταμείο (frontend/src/utils/salaFloor): βλ. floor.ts.
// Οι προεπιλεγμένες ετικέτες (π.χ. «Είσοδος») είναι παράμετροι με ελληνική προεπιλογή, ώστε
// το ταμείο να τις δίνει στη γλώσσα του.

import { boxOf, freeSpot, nextNumber, roomAt, tableSize, type Fixture, type FixtureType, type FloorDoc, type FTable, type Opening, type OpeningType, type Room, type Shape, type Side } from './floor';

export const GRID = 10; // 20 εκ.
const snapTo = (v: number, on: boolean) => (on ? Math.round(v / GRID) * GRID : Math.round(v));
let seq = 0;
export const uid = (p: string) => `${p}${Date.now().toString(36)}${(seq++).toString(36)}`;

export type DragKind = 't' | 'fx' | 'rm' | 'rh' | 'op';
export interface Drag { kind: DragKind; key: string; room?: string; handle?: string }
export interface Guide { v: boolean; at: number; a: number; b: number }

const mapT = (doc: FloorDoc, key: string, f: (t: FTable) => FTable): FloorDoc => ({ ...doc, tables: doc.tables.map(t => (t.key === key ? f(t) : t)) });
const mapR = (doc: FloorDoc, key: string, f: (r: Room) => Room): FloorDoc => ({ ...doc, rooms: doc.rooms.map(r => (r.key === key ? f(r) : r)) });

/** Σύρσιμο: πλέγμα 20 εκ. και ευθυγράμμιση με τα άλλα τραπέζια (οδηγοί). */
export function applyDrag(doc: FloorDoc, d: Drag, dx: number, dy: number, snap: boolean): { doc: FloorDoc; guides: Guide[] } {
  const guides: Guide[] = [];
  if (d.kind === 't') {
    const t0 = doc.tables.find(t => t.key === d.key);
    if (!t0) return { doc, guides };
    let x = snapTo(t0.x + dx, snap);
    let y = snapTo(t0.y + dy, snap);
    if (snap) {
      let bx: FTable | null = null;
      let by: FTable | null = null;
      for (const o of doc.tables) {
        if (o.key === d.key) continue;
        if (Math.abs(o.x - x) <= 8 && (!bx || Math.abs(o.x - x) < Math.abs(bx.x - x))) bx = o;
        if (Math.abs(o.y - y) <= 8 && (!by || Math.abs(o.y - y) < Math.abs(by.y - y))) by = o;
      }
      if (bx) { x = bx.x; guides.push({ v: true, at: x, a: Math.min(y, bx.y) - 60, b: Math.max(y, bx.y) + 60 }); }
      if (by) { y = by.y; guides.push({ v: false, at: y, a: Math.min(x, by.x) - 80, b: Math.max(x, by.x) + 80 }); }
    }
    return { doc: mapT(doc, d.key, t => ({ ...t, x, y })), guides };
  }
  if (d.kind === 'fx') {
    return { doc: { ...doc, fixtures: doc.fixtures.map(f => (f.id === d.key ? { ...f, x: snapTo(f.x + dx, snap), y: snapTo(f.y + dy, snap) } : f)) }, guides };
  }
  const r0 = doc.rooms.find(r => r.key === (d.kind === 'op' ? d.room : d.key));
  if (!r0) return { doc, guides };
  if (d.kind === 'rm') {
    const mx = snapTo(r0.x + dx, snap) - r0.x;
    const my = snapTo(r0.y + dy, snap) - r0.y;
    const inRoom = (x: number, y: number) => x >= r0.x && x <= r0.x + r0.w && y >= r0.y && y <= r0.y + r0.h;
    return {
      doc: {
        ...doc,
        rooms: doc.rooms.map(r => (r.key === r0.key ? { ...r, x: r.x + mx, y: r.y + my } : r)),
        tables: doc.tables.map(t => (inRoom(t.x, t.y) ? { ...t, x: t.x + mx, y: t.y + my } : t)),
        fixtures: doc.fixtures.map(f => (inRoom(f.x + f.w / 2, f.y + f.h / 2) ? { ...f, x: f.x + mx, y: f.y + my } : f)),
      },
      guides,
    };
  }
  if (d.kind === 'rh') {
    const h = d.handle || '';
    let { x, y, w, h: hh } = r0;
    if (h.includes('e')) w = Math.max(160, snapTo(r0.w + dx, snap));
    if (h.includes('s')) hh = Math.max(100, snapTo(r0.h + dy, snap));
    if (h.includes('w')) { const nx = Math.min(snapTo(r0.x + dx, snap), r0.x + r0.w - 160); w = r0.w + (r0.x - nx); x = nx; }
    if (h.includes('n')) { const ny = Math.min(snapTo(r0.y + dy, snap), r0.y + r0.h - 100); hh = r0.h + (r0.y - ny); y = ny; }
    const openings = r0.openings.map(o => {
      const span = o.side === 'top' || o.side === 'bottom' ? w : hh;
      return { ...o, len: Math.min(o.len, span - 16), at: Math.max(8, Math.min(o.at, span - Math.min(o.len, span - 16) - 8)) };
    });
    return { doc: mapR(doc, r0.key, r => ({ ...r, x, y, w, h: hh, openings })), guides };
  }
  // 'op': το άνοιγμα γλιστρά πάνω στον τοίχο του
  const o0 = r0.openings.find(o => o.id === d.key);
  if (!o0) return { doc, guides };
  const horiz = o0.side === 'top' || o0.side === 'bottom';
  const span = horiz ? r0.w : r0.h;
  const at = Math.max(8, Math.min(snapTo(o0.at + (horiz ? dx : dy), snap), span - o0.len - 8));
  return { doc: mapR(doc, r0.key, r => ({ ...r, openings: r.openings.map(o => (o.id === o0.id ? { ...o, at } : o)) })), guides };
}

export function patchTable(doc: FloorDoc, key: string, patch: Partial<FTable>): FloorDoc {
  return mapT(doc, key, t => {
    const next = { ...t, ...patch };
    // Σχήμα ή θέσεις αλλάζουν το μέγεθος (το περιστραμμένο μακρόστενο μένει όρθιο).
    if (patch.shape !== undefined || patch.cap !== undefined) {
      const [w, h] = tableSize(next.shape, next.cap);
      const upright = t.shape === 'rectangle' && next.shape === 'rectangle' && t.h > t.w;
      next.w = upright ? h : w;
      next.h = upright ? w : h;
      next.minOnline = Math.min(next.minOnline, next.cap);
    }
    return next;
  });
}

export function rotateTable(doc: FloorDoc, key: string): FloorDoc {
  return mapT(doc, key, t => ({ ...t, w: t.h, h: t.w }));
}

export function patchRoom(doc: FloorDoc, key: string, patch: Partial<Room>): FloorDoc {
  return mapR(doc, key, r => ({ ...r, ...patch }));
}

export function patchOpening(doc: FloorDoc, room: string, id: string, patch: Partial<Opening>): FloorDoc {
  return mapR(doc, room, r => ({ ...r, openings: r.openings.map(o => (o.id === id ? { ...o, ...patch } : o)) }));
}

export function patchFixture(doc: FloorDoc, key: string, patch: Partial<Fixture>): FloorDoc {
  return { ...doc, fixtures: doc.fixtures.map(f => (f.id === key ? { ...f, ...patch } : f)) };
}

export function addTable(doc: FloorDoc, room: Room, shape: Shape, cap: number): { doc: FloorDoc; key: string; n: string } {
  const [w, h] = tableSize(shape, cap);
  const [x, y] = freeSpot(doc, room, w, h);
  const t: FTable = {
    key: uid('t'), id: null, n: nextNumber(doc.tables), name: null, shape, x, y, w, h, cap,
    res: true, online: true, minOnline: 1, prio: 0, hasHistory: false, inUse: false, upcoming: [],
  };
  return { doc: { ...doc, tables: [...doc.tables, t] }, key: t.key, n: t.n };
}

export function duplicateTable(doc: FloorDoc, key: string): { doc: FloorDoc; key: string; n: string } | null {
  const t = doc.tables.find(x => x.key === key);
  if (!t) return null;
  const room = roomAt(doc.rooms, t.x, t.y) || doc.rooms[0];
  const [x, y] = freeSpot(doc, room, t.w, t.h);
  const c: FTable = { ...t, key: uid('t'), id: null, n: nextNumber(doc.tables), x, y, hasHistory: false, inUse: false, upcoming: [] };
  return { doc: { ...doc, tables: [...doc.tables, c] }, key: c.key, n: c.n };
}

export function removeTable(doc: FloorDoc, key: string): FloorDoc {
  return {
    ...doc,
    tables: doc.tables.filter(t => t.key !== key),
    combos: doc.combos.filter(c => !c.tables.includes(key)),
  };
}

/** Νέο άνοιγμα στον πρώτο τοίχο που έχει ελεύθερο χώρο (κάτω, πάνω, αριστερά, δεξιά). */
export function addOpening(doc: FloorDoc, room: Room, type: OpeningType, entranceLabel = 'Είσοδος'): { doc: FloorDoc; id: string } {
  const len = type === 'window' ? 160 : type === 'entrance' ? 64 : 46;
  let side: Side = 'bottom';
  let at = 8;
  for (const sd of ['bottom', 'top', 'left', 'right'] as Side[]) {
    const span = sd === 'top' || sd === 'bottom' ? room.w : room.h;
    const taken = room.openings.filter(o => o.side === sd);
    let found: number | null = null;
    for (let a = 20; a <= span - len - 20; a += 10) {
      if (!taken.some(o => a < o.at + o.len + 10 && o.at < a + len + 10)) { found = a; break; }
    }
    if (found != null) { side = sd; at = found; break; }
  }
  const o: Opening = { id: uid('o'), type, side, at, len, label: type === 'entrance' ? entranceLabel : '' };
  return { doc: mapR(doc, room.key, r => ({ ...r, openings: [...r.openings, o] })), id: o.id };
}

export function removeOpening(doc: FloorDoc, room: string, id: string): FloorDoc {
  return mapR(doc, room, r => ({ ...r, openings: r.openings.filter(o => o.id !== id) }));
}

const FIXTURE_SIZE: Record<FixtureType, [number, number, string]> = {
  bar: [160, 40, 'ΜΠΑΡ'], column: [24, 24, ''], plant: [24, 24, ''], label: [90, 26, 'WC'],
};

export function addFixture(doc: FloorDoc, room: Room, type: FixtureType, labelText?: string): { doc: FloorDoc; key: string } {
  const [w, h, defLabel] = FIXTURE_SIZE[type];
  const label = labelText ?? defLabel;
  // Σε ελεύθερο σημείο (όχι πάνω σε τραπέζια): το freeSpot δίνει κέντρο.
  const [cx, cy] = freeSpot(doc, room, w, h);
  const f: Fixture = { id: uid('f'), type, w, h, label, x: cx - w / 2, y: cy - h / 2 };
  return { doc: { ...doc, fixtures: [...doc.fixtures, f] }, key: f.id };
}

export function removeFixture(doc: FloorDoc, key: string): FloorDoc {
  return { ...doc, fixtures: doc.fixtures.filter(f => f.id !== key) };
}

/** Νέα περιοχή κάτω από τις υπόλοιπες. */
export function addRoom(doc: FloorDoc, baseName = 'Νέα περιοχή'): { doc: FloorDoc; key: string } {
  const maxY = doc.rooms.length ? Math.max(...doc.rooms.map(r => r.y + r.h)) : -30;
  const names = new Set(doc.rooms.map(r => r.name.trim()));
  let name = baseName;
  for (let i = 2; names.has(name); i++) name = `${baseName} ${i}`;
  const r: Room = { key: uid('z'), id: null, name, kind: 'indoor', x: 0, y: maxY + 70, w: 400, h: 220, openings: [], printerId: null };
  return { doc: { ...doc, rooms: [...doc.rooms, r] }, key: r.key };
}

export function tablesIn(doc: FloorDoc, room: Room): FTable[] {
  return doc.tables.filter(t => t.x >= room.x && t.x <= room.x + room.w && t.y >= room.y && t.y <= room.y + room.h);
}

export function removeRoom(doc: FloorDoc, key: string): FloorDoc {
  return { ...doc, rooms: doc.rooms.filter(r => r.key !== key) };
}

export function link(doc: FloorDoc, a: string, b: string): { doc: FloorDoc; added: boolean } {
  if (a === b || doc.combos.some(c => c.tables.includes(a) && c.tables.includes(b))) return { doc, added: false };
  return { doc: { ...doc, combos: [...doc.combos, { key: uid('c'), id: null, tables: [a, b], online: false }] }, added: true };
}

export function unlink(doc: FloorDoc, comboKey: string): FloorDoc {
  return { ...doc, combos: doc.combos.filter(c => c.key !== comboKey) };
}

export function comboLabel(doc: FloorDoc, tables: string[], upTo = 'έως', prefix = 'Τ'): string {
  const ts = tables.map(k => doc.tables.find(t => t.key === k)).filter((t): t is FTable => !!t);
  return `${ts.map(t => `${prefix}${t.n}`).join('+')} · ${upTo} ${ts.reduce((s, t) => s + t.cap, 0)}`;
}

/** Οδηγός πρώτου στησίματος: χώροι και πόσα 2άρια/4άρια/6άρια → κάτοψη σε σειρές. */
export interface SetupArea { name: string; kind: 'indoor' | 'outdoor'; counts: [number, number, number]; start: number }
export function generateFloor(areas: SetupArea[]): FloorDoc {
  const SIZES: [Shape, number][] = [['round', 2], ['square', 4], ['rectangle', 6]];
  const ROOM_W = 620;
  const doc: FloorDoc = { rooms: [], fixtures: [], tables: [], combos: [] };
  let y = 34;
  for (const a of areas) {
    const list: [Shape, number][] = [];
    [2, 1, 0].forEach(i => { for (let j = 0; j < a.counts[i]; j++) list.push(SIZES[i]); });
    const placed: { shape: Shape; cap: number; x: number; y: number }[] = [];
    let x = 40;
    let row = 0;
    for (const [shape, cap] of list) {
      const [w] = tableSize(shape, cap);
      if (x + w > ROOM_W - 40) { x = 40; row += 104; }
      placed.push({ shape, cap, x: x + w / 2, y: row });
      x += w + 44;
    }
    const h = (list.length ? row + 76 : 40) + 64;
    const room: Room = { key: uid('z'), id: null, name: a.name, kind: a.kind, x: 0, y, w: ROOM_W, h, openings: [], printerId: null };
    doc.rooms.push(room);
    placed.forEach((p, i) => {
      const [w, hh] = tableSize(p.shape, p.cap);
      doc.tables.push({
        key: uid('t'), id: null, n: String(a.start + i), name: null, shape: p.shape, x: p.x, y: y + 32 + p.y + 38, w, h: hh, cap: p.cap,
        res: true, online: true, minOnline: 1, prio: 0, hasHistory: false, inUse: false, upcoming: [],
      });
    });
    y += h + 72;
  }
  return doc;
}

export function selectionBox(doc: FloorDoc, key: string) {
  const t = doc.tables.find(x => x.key === key);
  return t ? boxOf(t) : null;
}
